import { FC, useState, useEffect } from "react";
import { Modal, Button, SelectBox } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CaseStatusApi from "@/api/case-status";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FormWrapper,
  FormTitle,
} from "@/components/Forms/components/MonolithFormComponents";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface CaseStatus {
  case_status_id: number;
  status_name: string;
  total: number;
}

interface DeleteCaseStatusModalProps {
  show: boolean;
  onClose: () => void;
  deleteSelection: CaseStatus | null;
  caseStatuses: CaseStatus[];
  onDelete?: () => void;
}

export const DeleteCaseStatusModal: FC<DeleteCaseStatusModalProps> = ({
  show,
  onClose,
  deleteSelection,
  caseStatuses,
  onDelete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();
  const [replacementStatus, setReplacementStatus] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Filter out the status being deleted
  const availableStatuses = caseStatuses.filter(
    (item) => item.case_status_id !== deleteSelection?.case_status_id
  );

  // Reset form when modal opens/closes or deleteSelection changes
  useEffect(() => {
    setReplacementStatus("");
    setError("");
  }, [show, deleteSelection]);

  const deleteMutation = useMutation({
    mutationFn: (params: {
      id: number;
      options?: { replace: string | null };
    }) =>
      CaseStatusApi.deleteCaseStatus(
        params.id,
        params.options as { replace: null } | undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-statuses", "list"] });

      enqueueSnackbar(`Case Status "${deleteSelection?.status_name}" deleted`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Deleted Case Status: ${deleteSelection?.status_name}`,
        event: "case_status:delete",
        object_type: "case_status",
      });

      onDelete?.();
      onClose();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while deleting this case status. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = () => {
    if (!deleteSelection) return;

    // Validate if replacement is required
    if (deleteSelection.total > 0 && !replacementStatus) {
      setError("Please select a replacement case status");
      return;
    }

    // Call API with or without replacement
    if (deleteSelection.total > 0) {
      const replacement = availableStatuses.find(
        (s) => s.case_status_id.toString() === replacementStatus
      );
      if (!replacement) {
        setError("Selected replacement status is no longer available");
        return;
      }
      deleteMutation.mutate({
        id: deleteSelection.case_status_id,
        options: { replace: replacement.status_name },
      });
    } else {
      deleteMutation.mutate({ id: deleteSelection.case_status_id });
    }
  };

  if (!deleteSelection) return null;

  return (
    <Modal open={show} onClose={onClose}>
      <FormWrapper width={500}>
        <FormTitle>Delete Case Status {deleteSelection.status_name}</FormTitle>

        {deleteSelection.total === 0 && (
          <div style={{ marginBottom: "20px" }}>
            Are you sure you want to delete this case status?
          </div>
        )}

        {deleteSelection.total > 0 && (
          <div>
            <div style={{ marginBottom: "15px" }}>
              Select the case status that should replace this one when deleted:
            </div>
            <SelectBox
              size="sm"
              variant="outlined"
              label="Case Status"
              required={true}
              placeholder="Select replacement status"
              searchable
              data={availableStatuses.map((status) => ({
                value: status.case_status_id.toString(),
                label: status.status_name,
              }))}
              value={replacementStatus}
              onChange={(value: string | null) => {
                setReplacementStatus(value || "");
                setError("");
              }}
              error={error}
            />
          </div>
        )}

        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="red"
            variant="contained"
            onClick={handleSubmit}
            disabled={deleteMutation.isPending}
          >
            Delete Case Status
          </Button>
        </ButtonMenu>
      </FormWrapper>
    </Modal>
  );
};
